// src/api/pagoVenta.js - API para manejo de pagos de venta
import { apiFetch } from './index.js';

/**
 * Obtener todos los pagos de venta
 * @returns {Promise} Lista de pagos
 */
export const obtenerPagosVenta = async () => {
  try {
    console.log('📋 Obteniendo todos los pagos de venta...');
    
    const response = await apiFetch('/api/PagoVenta');
    
    // Normalizar los datos
    const data = Array.isArray(response) ? response : (response.data || []);
    const pagosNormalizados = data.map(pago => ({
      ...pago,
      idPagoVenta: pago.idPagoVenta || pago.id_pago_venta,
      idVenta: pago.idVenta || pago.id_venta,
      idCuota: pago.idCuota || pago.id_cuota,
      fechaPago: pago.fechaPago || pago.fecha_pago,
      montoUsd: pago.montoUsd ?? pago.monto_usd ?? 0,
      montoBs: pago.montoBs ?? pago.monto_bs ?? 0,
      tipoPago: pago.tipoPago || pago.tipo_pago || pago.metodoPago || pago.metodo_pago,
      numeroRecibo: pago.numeroRecibo || pago.numero_recibo,
      observaciones: pago.observaciones,
      fecha_creacion: pago.fecha_creacion || pago.fechaCreacion
    }));
    
    return {
      success: true,
      data: pagosNormalizados,
      count: pagosNormalizados.length,
      message: 'Pagos obtenidos exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error obteniendo pagos:', error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Obtener un pago por ID
 * @param {number} id - ID del pago
 * @returns {Promise} Datos del pago
 */
export const obtenerPagoVentaPorId = async (id) => {
  try {
    if (!id) {
      throw new Error('ID de pago es requerido');
    }
    
    console.log(`🔍 Obteniendo pago ID: ${id}`);
    
    const response = await apiFetch(`/api/PagoVenta/${id}`);
    
    const data = response.data || response;
    
    return {
      success: true,
      data: {
        idPagoVenta: data.idPagoVenta,
        idVenta: data.idVenta,
        idCuota: data.idCuota,
        fechaPago: data.fechaPago,
        montoUsd: data.montoUsd || 0,
        montoBs: data.montoBs || 0,
        tipoPago: data.tipoPago,
        numeroRecibo: data.numeroRecibo,
        observaciones: data.observaciones
      },
      message: response.message || 'Pago obtenido exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo pago ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Obtener pagos por ID de venta
 * @param {number} idVenta - ID de la venta
 * @returns {Promise} Lista de pagos de la venta
 */
export const obtenerPagosPorVenta = async (idVenta) => {
  try {
    if (!idVenta) {
      throw new Error('ID de venta es requerido');
    }
    
    console.log(`📋 Obteniendo pagos de venta ID: ${idVenta}`);
    
    const response = await apiFetch(`/api/PagoVenta/venta/${idVenta}`);
    
    const data = Array.isArray(response) ? response : (response.data || []);
    const pagosNormalizados = data.map(pago => ({
      idPagoVenta: pago.idPagoVenta,
      idVenta: pago.idVenta,
      idCuota: pago.idCuota,
      fechaPago: pago.fechaPago,
      montoUsd: pago.montoUsd || 0,
      montoBs: pago.montoBs || 0,
      tipoPago: pago.tipoPago,
      numeroRecibo: pago.numeroRecibo,
      observaciones: pago.observaciones
    }));
    
    return {
      success: true,
      data: pagosNormalizados,
      count: pagosNormalizados.length,
      message: 'Pagos de venta obtenidos exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo pagos de venta ${idVenta}:`, error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Obtener pagos por ID de cuota
 * @param {number} idCuota - ID de la cuota
 * @returns {Promise} Lista de pagos de la cuota
 */
export const obtenerPagosPorCuota = async (idCuota) => {
  try {
    if (!idCuota) {
      throw new Error('ID de cuota es requerido');
    }
    
    console.log(`📋 Obteniendo pagos de cuota ID: ${idCuota}`);
    
    const response = await apiFetch(`/api/PagoVenta/cuota/${idCuota}`);
    
    const data = Array.isArray(response) ? response : (response.data || []);
    const pagosNormalizados = data.map(pago => ({
      idPagoVenta: pago.idPagoVenta,
      idVenta: pago.idVenta,
      idCuota: pago.idCuota,
      fechaPago: pago.fechaPago,
      montoUsd: pago.montoUsd || 0,
      montoBs: pago.montoBs || 0,
      tipoPago: pago.tipoPago,
      numeroRecibo: pago.numeroRecibo,
      observaciones: pago.observaciones
    }));
    
    return {
      success: true,
      data: pagosNormalizados,
      count: pagosNormalizados.length,
      message: 'Pagos de cuota obtenidos exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo pagos de cuota ${idCuota}:`, error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Obtener pagos por tipo de pago
 * @param {string} tipoPago - Tipo de pago (efectivo, transferencia, tarjeta, etc.)
 * @returns {Promise} Lista de pagos del tipo especificado
 */
export const obtenerPagosPorTipo = async (tipoPago) => {
  try {
    if (!tipoPago) {
      throw new Error('Tipo de pago es requerido');
    }
    
    console.log(`📋 Obteniendo pagos tipo: ${tipoPago}`);
    
    const response = await apiFetch(`/api/PagoVenta/tipo/${encodeURIComponent(tipoPago)}`);
    
    const data = Array.isArray(response) ? response : (response.data || []);
    const pagosNormalizados = data.map(pago => ({
      idPagoVenta: pago.idPagoVenta,
      idVenta: pago.idVenta,
      idCuota: pago.idCuota,
      fechaPago: pago.fechaPago,
      montoUsd: pago.montoUsd || 0,
      montoBs: pago.montoBs || 0,
      tipoPago: pago.tipoPago,
      numeroRecibo: pago.numeroRecibo,
      observaciones: pago.observaciones
    }));
    
    return {
      success: true,
      data: pagosNormalizados,
      count: pagosNormalizados.length,
      message: `Pagos tipo ${tipoPago} obtenidos exitosamente`
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo pagos tipo ${tipoPago}:`, error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Obtener pagos por rango de fechas
 * @param {string} desde - Fecha inicial (ISO)
 * @param {string} hasta - Fecha final (ISO)
 * @returns {Promise} Lista de pagos en el rango
 */
export const obtenerPagosPorFecha = async (desde, hasta) => {
  try {
    if (!desde || !hasta) {
      throw new Error('Fechas desde y hasta son requeridas');
    }
    
    console.log(`📅 Obteniendo pagos entre ${desde} y ${hasta}`);
    
    const response = await apiFetch(`/api/PagoVenta/fecha/${desde}/${hasta}`);
    
    const data = Array.isArray(response) ? response : (response.data || []);
    const pagosNormalizados = data.map(pago => ({
      idPagoVenta: pago.idPagoVenta,
      idVenta: pago.idVenta,
      idCuota: pago.idCuota,
      fechaPago: pago.fechaPago,
      montoUsd: pago.montoUsd || 0,
      montoBs: pago.montoBs || 0,
      tipoPago: pago.tipoPago,
      numeroRecibo: pago.numeroRecibo,
      observaciones: pago.observaciones
    }));
    
    return {
      success: true,
      data: pagosNormalizados,
      count: pagosNormalizados.length,
      message: 'Pagos en rango obtenidos exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error obteniendo pagos por fecha:', error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Obtener resumen de pagos por venta
 * @param {number} idVenta - ID de la venta
 * @returns {Promise} Resumen de pagos
 */
export const obtenerResumenPagosVenta = async (idVenta) => {
  try {
    if (!idVenta) {
      throw new Error('ID de venta es requerido');
    }
    
    console.log(`📊 Obteniendo resumen de pagos para venta ID: ${idVenta}`);
    
    const response = await apiFetch(`/api/PagoVenta/venta/${idVenta}/resumen`);
    
    return {
      success: true,
      data: response.data || response,
      message: response.message || 'Resumen obtenido exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo resumen de venta ${idVenta}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Obtener pago con relaciones
 * @param {number} id - ID del pago
 * @returns {Promise} Datos del pago con relaciones
 */
export const obtenerPagoConRelaciones = async (id) => {
  try {
    if (!id) {
      throw new Error('ID de pago es requerido');
    }
    
    console.log(`🔍 Obteniendo pago con relaciones ID: ${id}`);
    
    const response = await apiFetch(`/api/PagoVenta/${id}/conRelaciones`);
    
    return {
      success: true,
      data: response.data || response,
      message: response.message || 'Pago obtenido exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo pago con relaciones ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Buscar pagos por término
 * @param {string} termino - Término de búsqueda
 * @returns {Promise} Lista de pagos que coinciden
 */
export const buscarPagosAPI = async (termino) => {
  try {
    if (!termino || termino.trim() === '') {
      throw new Error('Término de búsqueda es requerido');
    }
    
    console.log(`🔎 Buscando pagos: "${termino}"`);
    
    const response = await apiFetch('/api/PagoVenta/search', {
      method: 'POST',
      body: JSON.stringify({ termino: termino.trim() })
    });
    
    const data = response.data || response;
    
    return {
      success: true,
      data: Array.isArray(data) ? data : [],
      count: Array.isArray(data) ? data.length : 0,
      message: response.message || 'Búsqueda completada'
    };
    
  } catch (error) {
    console.error(`❌ Error buscando pagos "${termino}":`, error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Verificar si un pago existe por ID
 * @param {number} id - ID del pago
 * @returns {Promise} Resultado de la verificación
 */
export const verificarPagoExistente = async (id) => {
  try {
    if (!id) {
      throw new Error('ID de pago es requerido');
    }
    
    console.log(`🔎 Verificando pago ID: ${id}`);
    
    const response = await apiFetch(`/api/PagoVenta/${id}/exists`);
    
    return {
      success: true,
      existe: response.exists || response.data?.exists || false,
      message: response.message || (response.exists ? 'El pago existe' : 'El pago no existe')
    };
    
  } catch (error) {
    console.error(`❌ Error verificando pago ${id}:`, error);
    return {
      success: false,
      error: error.message,
      existe: false
    };
  }
};

/**
 * Crear un nuevo pago de venta
 * @param {Object} pagoData - Datos del pago
 * @returns {Promise} Pago creado
 */
export const crearPagoVenta = async (pagoData) => {
  try {
    // Validar datos requeridos
    if (!pagoData.idVenta && !pagoData.idCuota) {
      throw new Error('Se requiere ID de venta o ID de cuota');
    }
    
    if (!pagoData.montoUsd && !pagoData.montoBs) {
      throw new Error('Se requiere al menos un monto (USD o Bs)');
    }
    
    if (!pagoData.tipoPago || pagoData.tipoPago.trim() === '') {
      throw new Error('El tipo de pago es requerido');
    }
    
    console.log('➕ Creando nuevo pago de venta:', pagoData);
    
    const response = await apiFetch('/api/PagoVenta', {
      method: 'POST',
      body: JSON.stringify({
        idVenta: pagoData.idVenta || 0,
        idCuota: pagoData.idCuota || 0,
        fechaPago: pagoData.fechaPago || new Date().toISOString(),
        montoUsd: parseFloat(pagoData.montoUsd) || 0,
        montoBs: parseFloat(pagoData.montoBs) || 0,
        tipoPago: pagoData.tipoPago.trim(),
        numeroRecibo: pagoData.numeroRecibo || null,
        observaciones: pagoData.observaciones || null
      })
    });
    
    return {
      success: true,
      data: response.data || response,
      message: response.message || 'Pago registrado exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error creando pago:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Actualizar un pago existente
 * @param {number} id - ID del pago
 * @param {Object} pagoData - Datos a actualizar
 * @returns {Promise} Pago actualizado
 */
export const actualizarPagoVenta = async (id, pagoData) => {
  try {
    if (!id) {
      throw new Error('ID de pago es requerido');
    }
    
    if (!pagoData.tipoPago || pagoData.tipoPago.trim() === '') {
      throw new Error('El tipo de pago es requerido');
    }
    
    console.log(`✏️ Actualizando pago ID: ${id}`, pagoData);
    
    const payload = {
      idPagoVenta: parseInt(id),
      idVenta: pagoData.idVenta || 0,
      idCuota: pagoData.idCuota || 0,
      fechaPago: pagoData.fechaPago || new Date().toISOString(),
      montoUsd: parseFloat(pagoData.montoUsd) || 0,
      montoBs: parseFloat(pagoData.montoBs) || 0,
      tipoPago: pagoData.tipoPago.trim(),
      numeroRecibo: pagoData.numeroRecibo || null,
      observaciones: pagoData.observaciones || null
    };
    
    console.log('📦 Payload enviado a la API:', payload);
    
    const response = await apiFetch(`/api/PagoVenta/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload)
    });
    
    return {
      success: true,
      data: response.data || response,
      message: response.message || 'Pago actualizado exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error actualizando pago ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Eliminar un pago
 * @param {number} id - ID del pago a eliminar
 * @returns {Promise} Resultado de la eliminación
 */
export const eliminarPagoVenta = async (id) => {
  try {
    if (!id) {
      throw new Error('ID de pago es requerido');
    }
    
    console.log(`🗑️ Eliminando pago ID: ${id}`);
    
    const response = await apiFetch(`/api/PagoVenta/${id}`, {
      method: 'DELETE'
    });
    
    return {
      success: true,
      data: response.data || response,
      message: response.message || 'Pago eliminado exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error eliminando pago ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Anular un pago
 * @param {number} id - ID del pago a anular
 * @returns {Promise} Resultado de la anulación
 */
export const anularPagoVenta = async (id) => {
  try {
    if (!id) {
      throw new Error('ID de pago es requerido');
    }
    
    console.log(`🚫 Anulando pago ID: ${id}`);
    
    const response = await apiFetch(`/api/PagoVenta/${id}/anular`, {
      method: 'DELETE'
    });
    
    return {
      success: true,
      data: response.data || response,
      message: response.message || 'Pago anulado exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error anulando pago ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

// ==================== FUNCIONES AUXILIARES (CLIENTE-SIDE) ====================

/**
 * Buscar pagos por término (cliente-side)
 * @param {Array} pagos - Lista de pagos
 * @param {string} termino - Término de búsqueda
 * @returns {Array} Pagos filtrados
 */
export const buscarPagosLocal = (pagos, termino) => {
  if (!termino || termino.trim() === '') {
    return pagos;
  }
  
  const terminoLower = termino.toLowerCase();
  return pagos.filter(pago => 
    pago.numeroRecibo?.toLowerCase().includes(terminoLower) ||
    pago.tipoPago?.toLowerCase().includes(terminoLower) ||
    pago.observaciones?.toLowerCase().includes(terminoLower) ||
    pago.idVenta?.toString().includes(terminoLower) ||
    pago.idCuota?.toString().includes(terminoLower)
  );
};

/**
 * Filtrar pagos por tipo (cliente-side)
 * @param {Array} pagos - Lista de pagos
 * @param {string} tipo - Tipo de pago
 * @returns {Array} Pagos filtrados
 */
export const filtrarPagosPorTipoLocal = (pagos, tipo) => {
  if (!tipo || tipo === 'all') return pagos;
  return pagos.filter(p => p.tipoPago?.toLowerCase() === tipo.toLowerCase());
};

/**
 * Filtrar pagos por rango de fechas (cliente-side)
 * @param {Array} pagos - Lista de pagos
 * @param {Date} fechaInicio - Fecha inicial
 * @param {Date} fechaFin - Fecha final
 * @returns {Array} Pagos filtrados
 */
export const filtrarPagosPorFechaLocal = (pagos, fechaInicio, fechaFin) => {
  if (!fechaInicio && !fechaFin) return pagos;
  
  return pagos.filter(pago => {
    const fechaPago = new Date(pago.fechaPago);
    if (fechaInicio && fechaFin) {
      return fechaPago >= fechaInicio && fechaPago <= fechaFin;
    } else if (fechaInicio) {
      return fechaPago >= fechaInicio;
    } else if (fechaFin) {
      return fechaPago <= fechaFin;
    }
    return true;
  });
};

/**
 * Obtener estadísticas de pagos
 * @param {Array} pagos - Lista de pagos
 * @returns {Object} Estadísticas
 */
export const obtenerEstadisticasPagos = (pagos) => {
  // Montos totales
  const totalUsd = pagos.reduce((sum, p) => sum + (p.montoUsd || 0), 0);
  const totalBs = pagos.reduce((sum, p) => sum + (p.montoBs || 0), 0);
  
  // Pagos por tipo
  const pagosPorTipo = {};
  pagos.forEach(pago => {
    const tipo = pago.tipoPago || 'desconocido';
    pagosPorTipo[tipo] = (pagosPorTipo[tipo] || 0) + 1;
  });
  
  // Montos por tipo
  const montosPorTipo = {
    usd: {},
    bs: {}
  };
  pagos.forEach(pago => {
    const tipo = pago.tipoPago || 'desconocido';
    if (pago.montoUsd) {
      montosPorTipo.usd[tipo] = (montosPorTipo.usd[tipo] || 0) + pago.montoUsd;
    }
    if (pago.montoBs) {
      montosPorTipo.bs[tipo] = (montosPorTipo.bs[tipo] || 0) + pago.montoBs;
    }
  });
  
  // Pagos con recibo
  const conRecibo = pagos.filter(p => p.numeroRecibo && p.numeroRecibo.trim() !== '').length;
  
  // Pagos con observaciones
  const conObservaciones = pagos.filter(p => p.observaciones && p.observaciones.trim() !== '').length;
  
  // Pagos por día (últimos 30 días)
  const hoy = new Date();
  const hace30Dias = new Date();
  hace30Dias.setDate(hace30Dias.getDate() - 30);
  
  const pagosRecientes = pagos.filter(p => {
    try {
      const fechaPago = new Date(p.fechaPago);
      return fechaPago >= hace30Dias;
    } catch {
      return false;
    }
  }).length;
  
  return {
    total: pagos.length,
    totalUsd,
    totalBs,
    totalGeneral: {
      usd: totalUsd,
      bs: totalBs
    },
    pagosPorTipo,
    montosPorTipo,
    conRecibo,
    porcentajeConRecibo: pagos.length > 0 ? Math.round((conRecibo / pagos.length) * 100) : 0,
    conObservaciones,
    porcentajeConObservaciones: pagos.length > 0 ? Math.round((conObservaciones / pagos.length) * 100) : 0,
    pagosRecientes,
    porcentajeRecientes: pagos.length > 0 ? Math.round((pagosRecientes / pagos.length) * 100) : 0
  };
};

/**
 * Ordenar pagos por diferentes criterios
 * @param {Array} pagos - Lista de pagos
 * @param {string} criterio - Criterio de ordenamiento
 * @returns {Array} Pagos ordenados
 */
export const ordenarPagos = (pagos, criterio = 'fecha_desc') => {
  const pagosCopy = [...pagos];
  
  switch (criterio) {
    case 'fecha_desc':
      return pagosCopy.sort((a, b) => new Date(b.fechaPago) - new Date(a.fechaPago));
    case 'fecha':
      return pagosCopy.sort((a, b) => new Date(a.fechaPago) - new Date(b.fechaPago));
    case 'montoUsd_desc':
      return pagosCopy.sort((a, b) => (b.montoUsd || 0) - (a.montoUsd || 0));
    case 'montoUsd':
      return pagosCopy.sort((a, b) => (a.montoUsd || 0) - (b.montoUsd || 0));
    case 'montoBs_desc':
      return pagosCopy.sort((a, b) => (b.montoBs || 0) - (a.montoBs || 0));
    case 'montoBs':
      return pagosCopy.sort((a, b) => (a.montoBs || 0) - (b.montoBs || 0));
    case 'tipoPago':
      return pagosCopy.sort((a, b) => (a.tipoPago || '').localeCompare(b.tipoPago || ''));
    case 'recibo':
      return pagosCopy.sort((a, b) => (a.numeroRecibo || '').localeCompare(b.numeroRecibo || ''));
    case 'venta':
      return pagosCopy.sort((a, b) => (a.idVenta || 0) - (b.idVenta || 0));
    default:
      return pagosCopy;
  }
};

/**
 * Agrupar pagos por venta
 * @param {Array} pagos - Lista de pagos
 * @returns {Object} Pagos agrupados por ID de venta
 */
export const agruparPagosPorVenta = (pagos) => {
  const grouped = {};
  pagos.forEach(pago => {
    const ventaId = pago.idVenta;
    if (!grouped[ventaId]) {
      grouped[ventaId] = [];
    }
    grouped[ventaId].push(pago);
  });
  return grouped;
};

/**
 * Agrupar pagos por cuota
 * @param {Array} pagos - Lista de pagos
 * @returns {Object} Pagos agrupados por ID de cuota
 */
export const agruparPagosPorCuota = (pagos) => {
  const grouped = {};
  pagos.forEach(pago => {
    const cuotaId = pago.idCuota;
    if (cuotaId && cuotaId > 0) {
      if (!grouped[cuotaId]) {
        grouped[cuotaId] = [];
      }
      grouped[cuotaId].push(pago);
    }
  });
  return grouped;
};

/**
 * Calcular total pagado por venta
 * @param {Array} pagos - Lista de pagos de una venta
 * @returns {Object} Totales por venta
 */
export const calcularTotalPagadoVenta = (pagos) => {
  const totalUsd = pagos.reduce((sum, p) => sum + (p.montoUsd || 0), 0);
  const totalBs = pagos.reduce((sum, p) => sum + (p.montoBs || 0), 0);
  
  return {
    totalUsd,
    totalBs,
    cantidadPagos: pagos.length,
    ultimoPago: pagos.length > 0 ? pagos.sort((a, b) => new Date(b.fechaPago) - new Date(a.fechaPago))[0] : null
  };
};

/**
 * Formatear fecha de pago
 * @param {string} fechaISO - Fecha en formato ISO
 * @param {boolean} completo - Si incluir hora completa
 * @returns {string} Fecha formateada
 */
export const formatearFechaPago = (fechaISO, completo = false) => {
  if (!fechaISO) return 'N/A';
  
  try {
    const fecha = new Date(fechaISO);
    const opciones = {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    };
    
    if (completo) {
      opciones.hour = '2-digit';
      opciones.minute = '2-digit';
    }
    
    return fecha.toLocaleDateString('es-ES', opciones);
  } catch (error) {
    return fechaISO;
  }
};

/**
 * Formatear tipo de pago a texto legible
 * @param {string} tipo - Tipo de pago
 * @returns {string} Tipo formateado
 */
export const formatearTipoPago = (tipo) => {
  if (!tipo) return 'No especificado';
  
  const tipos = {
    'efectivo': 'Efectivo',
    'transferencia': 'Transferencia',
    'tarjeta': 'Tarjeta',
    'punto': 'Punto de Venta',
    'mixto': 'Mixto'
  };
  
  return tipos[tipo.toLowerCase()] || tipo.charAt(0).toUpperCase() + tipo.slice(1);
};

/**
 * Formatear monto
 * @param {number} monto - Monto a formatear
 * @param {string} moneda - 'USD' o 'BS'
 * @returns {string} Monto formateado
 */
export const formatearMonto = (monto, moneda = 'USD') => {
  if (monto === undefined || monto === null) return 'N/A';
  
  const formato = new Intl.NumberFormat('es-VE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  
  return `${formato.format(monto)} ${moneda}`;
};

/**
 * Obtener color según tipo de pago
 * @param {string} tipo - Tipo de pago
 * @returns {string} Color en formato Tailwind
 */
export const getColorPorTipoPago = (tipo) => {
  if (!tipo) return 'bg-gray-100 text-gray-800';
  
  const colores = {
    'efectivo': 'bg-green-100 text-green-800',
    'transferencia': 'bg-blue-100 text-blue-800',
    'tarjeta': 'bg-purple-100 text-purple-800',
    'punto': 'bg-indigo-100 text-indigo-800',
    'mixto': 'bg-yellow-100 text-yellow-800'
  };
  
  return colores[tipo.toLowerCase()] || 'bg-gray-100 text-gray-800';
};

// ==================== FUNCIONES DE EXPORTACIÓN ====================

/**
 * Exportar pagos a Excel
 * @param {Array} pagos - Lista de pagos
 * @param {string} filename - Nombre del archivo (sin extensión)
 * @returns {Object} Resultado de la exportación
 */
export const exportarPagosExcel = (pagos, filename = 'pagos_venta') => {
  try {
    if (typeof window === 'undefined') {
      return {
        success: false,
        error: 'Exportación solo disponible en el navegador',
        filename: null
      };
    }
    
    const XLSX = require('xlsx');
    const wb = XLSX.utils.book_new();
    
    const data = pagos.map(pago => ({
      'ID Pago': pago.idPagoVenta || '',
      'ID Venta': pago.idVenta || '',
      'ID Cuota': pago.idCuota || '',
      'Fecha Pago': formatearFechaPago(pago.fechaPago, true),
      'Monto USD': pago.montoUsd || 0,
      'Monto Bs': pago.montoBs || 0,
      'Tipo Pago': formatearTipoPago(pago.tipoPago),
      'N° Recibo': pago.numeroRecibo || '',
      'Observaciones': pago.observaciones || ''
    }));
    
    const ws = XLSX.utils.json_to_sheet(data);
    
    const wscols = [
      { wch: 10 },  // ID Pago
      { wch: 10 },  // ID Venta
      { wch: 10 },  // ID Cuota
      { wch: 20 },  // Fecha
      { wch: 12 },  // Monto USD
      { wch: 12 },  // Monto Bs
      { wch: 15 },  // Tipo
      { wch: 15 },  // Recibo
      { wch: 40 }   // Observaciones
    ];
    ws['!cols'] = wscols;
    
    XLSX.utils.book_append_sheet(wb, ws, 'Pagos Venta');
    
    const fecha = new Date();
    const fechaStr = fecha.toISOString().split('T')[0];
    const nombreArchivo = `${filename}_${fechaStr}.xlsx`;
    
    XLSX.writeFile(wb, nombreArchivo);
    
    return {
      success: true,
      filename: nombreArchivo,
      message: `Archivo ${nombreArchivo} generado exitosamente`,
      count: pagos.length
    };
    
  } catch (error) {
    console.error('❌ Error exportando a Excel:', error);
    return {
      success: false,
      error: error.message,
      filename: null
    };
  }
};

/**
 * Exportar pagos a PDF
 * @param {Array} pagos - Lista de pagos
 * @param {string} titulo - Título del reporte
 * @param {string} logoBase64 - Logo en base64 (opcional)
 * @returns {Object} Resultado de la exportación
 */
export const exportarPagosPDF = (pagos, titulo = 'Reporte de Pagos de Venta', logoBase64 = null) => {
  try {
    console.log('📊 Exportando PDF de pagos...');
    
    if (typeof window === 'undefined') {
      return {
        success: false,
        error: 'Exportación solo disponible en el navegador',
        filename: null
      };
    }
    
    if (!pagos || pagos.length === 0) {
      throw new Error('No hay pagos para exportar');
    }
    
    let jsPDF;
    try {
      const jspdf = require('jspdf');
      jsPDF = jspdf.jsPDF;
      require('jspdf-autotable');
    } catch (e) {
      console.error('❌ jsPDF no está disponible:', e);
      return {
        success: false,
        error: 'La librería jsPDF no está disponible',
        filename: null
      };
    }
    
    const doc = new jsPDF('landscape');
    const fecha = new Date();
    const fechaStr = fecha.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    
    let y = 20;
    const marginLeft = 20;
    const pageWidth = doc.internal.pageSize.width;
    
    let logoAgregado = false;
    if (logoBase64) {
      try {
        doc.addImage(logoBase64, 'JPEG', marginLeft, 10, 30, 15);
        logoAgregado = true;
        y = 35;
      } catch (logoError) {
        console.warn('⚠️ Error al agregar logo:', logoError);
        y = 20;
      }
    }
    
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    const titleX = logoAgregado ? 60 : marginLeft;
    doc.text('JEAN INFORMATICO C.A.', titleX, y);
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(titulo, titleX, y + 8);
    
    y += 20;
    doc.setFontSize(10);
    doc.text(`Fecha: ${fechaStr}`, marginLeft, y);
    doc.text(`Total: ${pagos.length} pagos`, marginLeft + 80, y);
    
    y += 15;
    const estadisticas = obtenerEstadisticasPagos(pagos);
    
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Resumen Estadístico', marginLeft, y);
    
    y += 8;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    
    const resumen = [
      `Total USD: $${estadisticas.totalUsd.toFixed(2)}`,
      `Total Bs: Bs ${estadisticas.totalBs.toFixed(2)}`,
      `Con recibo: ${estadisticas.conRecibo} (${estadisticas.porcentajeConRecibo}%)`,
      `Con observaciones: ${estadisticas.conObservaciones} (${estadisticas.porcentajeConObservaciones}%)`
    ];
    
    resumen.forEach((linea, index) => {
      doc.text(`• ${linea}`, marginLeft + 5, y + (index * 5));
    });
    
    const tableData = pagos.map(pago => [
      pago.idPagoVenta || '',
      pago.idVenta || '',
      pago.idCuota || '',
      formatearFechaPago(pago.fechaPago),
      `$${(pago.montoUsd || 0).toFixed(2)}`,
      `Bs ${(pago.montoBs || 0).toFixed(2)}`,
      formatearTipoPago(pago.tipoPago),
      pago.numeroRecibo || '-',
      (pago.observaciones || '').substring(0, 30)
    ]);
    
    doc.autoTable({
      startY: y + 25,
      head: [['ID', 'Venta', 'Cuota', 'Fecha', 'USD', 'Bs', 'Tipo', 'Recibo', 'Observaciones']],
      body: tableData,
      theme: 'striped',
      headStyles: {
        fillColor: [79, 70, 229],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 8
      },
      columnStyles: {
        0: { cellWidth: 12 },
        1: { cellWidth: 12 },
        2: { cellWidth: 12 },
        3: { cellWidth: 25 },
        4: { cellWidth: 18 },
        5: { cellWidth: 18 },
        6: { cellWidth: 20 },
        7: { cellWidth: 20 },
        8: { cellWidth: 35 }
      },
      styles: {
        fontSize: 7,
        cellPadding: 2,
        overflow: 'linebreak'
      },
      didDrawPage: function(data) {
        doc.setFontSize(7);
        doc.setTextColor(100, 100, 100);
        doc.text('© Jean Informatico C.A. - Sistema de Gestión de Pagos', marginLeft, 195);
        doc.text(`Página ${data.pageNumber}`, pageWidth - marginLeft, 195, null, null, 'right');
      }
    });
    
    const fechaISO = fecha.toISOString().split('T')[0];
    const nombreArchivo = `pagos_venta_${fechaISO}.pdf`;
    doc.save(nombreArchivo);
    
    return {
      success: true,
      filename: nombreArchivo,
      message: 'PDF generado exitosamente',
      count: pagos.length,
      withLogo: logoAgregado
    };
    
  } catch (error) {
    console.error('❌ Error exportando PDF:', error);
    return {
      success: false,
      error: error.message,
      filename: null
    };
  }
};

// ==================== EXPORT DEFAULT ====================

export default {
  // Funciones API
  obtenerPagosVenta,
  obtenerPagoVentaPorId,
  obtenerPagosPorVenta,
  obtenerPagosPorCuota,
  obtenerPagosPorTipo,
  obtenerPagosPorFecha,
  obtenerResumenPagosVenta,
  obtenerPagoConRelaciones,
  buscarPagosAPI,
  verificarPagoExistente,
  crearPagoVenta,
  actualizarPagoVenta,
  eliminarPagoVenta,
  anularPagoVenta,
  
  // Funciones auxiliares
  formatearFechaPago,
  formatearTipoPago,
  formatearMonto,
  getColorPorTipoPago,
  buscarPagosLocal,
  filtrarPagosPorTipoLocal,
  filtrarPagosPorFechaLocal,
  obtenerEstadisticasPagos,
  ordenarPagos,
  agruparPagosPorVenta,
  agruparPagosPorCuota,
  calcularTotalPagadoVenta,
  
  // Funciones de exportación
  exportarPagosExcel,
  exportarPagosPDF
};